/**
 * SOLOMON — LEADS IMPORT
 * CSV upload + parse preview + dedup verdicts + confirmed ingestion.
 * Opens inside ToolModal from Tools menu.
 *
 * Two-step server flow:
 *   1. POST /api/leads/import-csv (multipart) → parse + dedup preview
 *   2. POST /api/leads/import-confirm (JSON, only checked rows) → insert
 *
 * Mapping override is rendered as a collapsible section. The default
 * PropStream map covers most columns; operator can edit per-import.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import './leads.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// ── Icons ──────────────────────────────────────────────────────

function UploadIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8"  x2="12" y2="13" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

// ── Verdict styling ────────────────────────────────────────────

const VERDICT_LABEL = {
  new:              'New',
  duplicate:        'Duplicate',
  co_owner:         'Co-owner',
  phone_collision:  'Phone match — different property',
};
const VERDICT_CLASS = {
  new:              'verdict-new',
  duplicate:        'verdict-dup',
  co_owner:         'verdict-co',
  phone_collision:  'verdict-phone',
};

// ── Component ──────────────────────────────────────────────────

export default function LeadsImport() {
  const { authFetch } = useAuth();

  const [phase, setPhase] = useState('idle'); // idle | parsing | preview | ingesting | done
  const [file, setFile]   = useState(null);
  const [sourceList, setSourceList] = useState('');
  const [error, setError] = useState(null);

  // Preview
  const [headers, setHeaders] = useState([]);
  const [defaultMapping, setDefaultMapping] = useState({});
  const [appliedMapping, setAppliedMapping] = useState({});
  const [rows, setRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [dropped, setDropped] = useState([]);
  const [verdictCounts, setVerdictCounts] = useState({});
  const [checked, setChecked] = useState(new Set());   // row_index of rows the operator wants to import
  const [showMapping, setShowMapping] = useState(false);

  // Result
  const [result, setResult] = useState(null);

  // Post-import skip-trace state
  // stStatus: idle | checking | confirm | running | done
  const [stStatus, setStStatus]   = useState('idle');
  const [stEstimate, setStEstimate] = useState(null); // { eligibleCount, estCents }
  const [stResult, setStResult]   = useState(null);   // { matched, traced } | { zeroEligible: true }
  const [stError, setStError]     = useState(null);

  const inputRef = useRef(null);

  // Drag-and-drop
  const [dragOver, setDragOver] = useState(false);

  const onDragOver = useCallback((e) => { e.preventDefault(); setDragOver(true); }, []);
  const onDragLeave = useCallback(() => setDragOver(false), []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }, []);

  const onPick = useCallback((e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  }, []);

  // ── Step 1: Upload + Preview ────────────────────────────────
  const runPreview = useCallback(async () => {
    if (!file) {
      setError('Pick a CSV file first');
      return;
    }
    setPhase('parsing');
    setError(null);

    try {
      const fd = new FormData();
      fd.append('file', file);
      if (sourceList.trim()) fd.append('source_list_name', sourceList.trim());

      const res = await authFetch(`${API_BASE}/api/leads/import-csv`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || `Upload failed (HTTP ${res.status})`);
        setPhase('idle');
        return;
      }

      const d = data.data;
      setHeaders(d.headers || []);
      setDefaultMapping(d.default_mapping || {});
      setAppliedMapping(d.applied_mapping || {});
      setRows(d.rows || []);
      setParseErrors(d.parse_errors || []);
      setDropped(d.dropped_for_missing_required || []);
      setVerdictCounts(d.verdict_counts || {});
      // Auto-check all non-duplicate rows
      const initial = new Set();
      for (const r of d.rows || []) {
        if (r._verdict !== 'duplicate') initial.add(r._row_index);
      }
      setChecked(initial);
      setPhase('preview');
    } catch (err) {
      setError(err.message || 'Preview failed');
      setPhase('idle');
    }
  }, [file, sourceList, authFetch]);

  const toggleRow = useCallback((rowIndex) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex); else next.add(rowIndex);
      return next;
    });
  }, []);

  const checkedRows = useMemo(
    () => rows.filter((r) => checked.has(r._row_index)),
    [rows, checked]
  );

  // ── Step 2: Confirm + Ingest ───────────────────────────────
  const runConfirm = useCallback(async () => {
    if (checkedRows.length === 0) {
      setError('Select at least one row to import');
      return;
    }
    setPhase('ingesting');
    setError(null);

    try {
      const res = await authFetch(`${API_BASE}/api/leads/import-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rows: checkedRows,
          source_list_name: sourceList.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || `Ingestion failed (HTTP ${res.status})`);
        setPhase('preview');
        return;
      }
      setResult(data.data);
      setPhase('done');
    } catch (err) {
      setError(err.message || 'Ingestion failed');
      setPhase('preview');
    }
  }, [checkedRows, sourceList, authFetch]);

  // ── Post-import skip-trace ─────────────────────────────────────
  const importSkipTraceCheck = useCallback(async () => {
    if (!result?.inserted?.length) return;
    setStStatus('checking');
    setStError(null);
    setStResult(null);
    try {
      const res = await authFetch(`${API_BASE}/api/leads/skip-trace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadIds: result.inserted.map((r) => r.id), dryRun: true }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Skip-trace check failed');
      const { eligibleCount, estCents } = data.data;
      if (eligibleCount === 0) {
        setStResult({ zeroEligible: true });
        setStStatus('done');
      } else {
        setStEstimate({ eligibleCount, estCents });
        setStStatus('confirm');
      }
    } catch (err) {
      setStError(err.message || 'Skip-trace check failed');
      setStStatus('idle');
    }
  }, [authFetch, result]);

  const importSkipTraceRun = useCallback(async () => {
    if (!result?.inserted?.length) return;
    setStStatus('running');
    setStError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/leads/skip-trace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadIds: result.inserted.map((r) => r.id) }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Skip-trace failed');
      const { matched, traced } = data.data;
      setStResult({ matched, traced });
      setStStatus('done');
    } catch (err) {
      setStError(err.message || 'Skip-trace failed');
      setStStatus('confirm');
    }
  }, [authFetch, result]);

  const importSkipTraceReset = useCallback(() => {
    setStStatus('idle');
    setStEstimate(null);
    setStResult(null);
    setStError(null);
  }, []);

  const reset = useCallback(() => {
    setPhase('idle');
    setFile(null);
    setSourceList('');
    setError(null);
    setHeaders([]);
    setDefaultMapping({});
    setAppliedMapping({});
    setRows([]);
    setParseErrors([]);
    setDropped([]);
    setVerdictCounts({});
    setChecked(new Set());
    setResult(null);
    setShowMapping(false);
    setStStatus('idle');
    setStEstimate(null);
    setStResult(null);
    setStError(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="leads-import">
      {error && (
        <div className="leads-import-error">
          <AlertIcon />
          <span>{error}</span>
        </div>
      )}

      {phase === 'idle' && (
        <div className="leads-import-stage">
          <label className="leads-import-label">Source list name <span className="leads-import-optional">(optional)</span></label>
          <input
            type="text"
            className="leads-import-input"
            placeholder="e.g. Birmingham Probate 2026-04-27"
            value={sourceList}
            onChange={(e) => setSourceList(e.target.value)}
          />

          <div
            className={`leads-import-drop ${dragOver ? 'is-drag' : ''} ${file ? 'has-file' : ''}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
          >
            <UploadIcon />
            {file ? (
              <>
                <div className="leads-import-filename">{file.name}</div>
                <div className="leads-import-filesize">{(file.size / 1024).toFixed(1)} KB</div>
              </>
            ) : (
              <>
                <div className="leads-import-drop-title">Drop CSV here</div>
                <div className="leads-import-drop-sub">or click to browse · PropStream export expected</div>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onPick}
              hidden
            />
          </div>

          <div className="leads-import-actions">
            <button
              className="btn-primary"
              onClick={runPreview}
              disabled={!file}
            >
              Parse + Preview
            </button>
          </div>
        </div>
      )}

      {phase === 'parsing' && (
        <div className="leads-import-stage leads-import-loading">
          <div className="leads-import-pulse" />
          <div>Parsing CSV and checking for duplicates…</div>
        </div>
      )}

      {phase === 'preview' && (
        <div className="leads-import-stage leads-import-preview">
          <div className="leads-import-summary">
            <span className="leads-import-summary-label">{rows.length} parsed</span>
            <span className="leads-import-summary-divider">·</span>
            <span className="leads-import-summary-pill verdict-new">{verdictCounts.new || 0} new</span>
            <span className="leads-import-summary-pill verdict-co">{verdictCounts.co_owner || 0} co-owner</span>
            <span className="leads-import-summary-pill verdict-phone">{verdictCounts.phone_collision || 0} phone match</span>
            <span className="leads-import-summary-pill verdict-dup">{verdictCounts.duplicate || 0} duplicate</span>
            {dropped.length > 0 && (
              <>
                <span className="leads-import-summary-divider">·</span>
                <span className="leads-import-summary-warn">{dropped.length} dropped (no address)</span>
              </>
            )}
          </div>

          <button
            type="button"
            className="leads-import-mapping-toggle"
            onClick={() => setShowMapping((v) => !v)}
          >
            {showMapping ? 'Hide' : 'Show'} field mapping ({Object.keys(appliedMapping).length} columns)
          </button>
          {showMapping && (
            <div className="leads-import-mapping">
              {headers.map((h) => {
                const mapped = appliedMapping[h];
                return (
                  <div key={h} className="leads-import-mapping-row">
                    <span className="leads-import-mapping-csv">{h}</span>
                    <span className="leads-import-mapping-arrow">→</span>
                    <span className={`leads-import-mapping-target ${mapped ? '' : 'is-unmapped'}`}>
                      {mapped || '(unmapped — kept in source_metadata)'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="leads-import-table-wrap">
            <table className="leads-import-table">
              <thead>
                <tr>
                  <th className="th-check"></th>
                  <th>#</th>
                  <th>Verdict</th>
                  <th>Address</th>
                  <th>Owner</th>
                  <th>Phone</th>
                  <th>Co-owner</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r._row_index}
                    className={r._verdict === 'duplicate' ? 'is-duplicate' : ''}
                  >
                    <td className="td-check">
                      <input
                        type="checkbox"
                        checked={checked.has(r._row_index)}
                        onChange={() => toggleRow(r._row_index)}
                      />
                    </td>
                    <td>{r._row_index}</td>
                    <td>
                      <span className={`leads-import-verdict ${VERDICT_CLASS[r._verdict] || ''}`}>
                        {VERDICT_LABEL[r._verdict] || r._verdict}
                      </span>
                    </td>
                    <td className="td-address">
                      {r.property_address}
                      {r.property_city && <span className="leads-import-city">, {r.property_city} {r.property_state}</span>}
                    </td>
                    <td>{r.owner_full_name || '—'}</td>
                    <td className="leads-import-mono">{r._phone_e164 || r.phone_primary || '—'}</td>
                    <td>{r.co_owner_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="leads-import-actions">
            <button className="btn-secondary" onClick={reset}>Cancel</button>
            <button
              className="btn-primary"
              onClick={runConfirm}
              disabled={checkedRows.length === 0}
            >
              Import {checkedRows.length} lead{checkedRows.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}

      {phase === 'ingesting' && (
        <div className="leads-import-stage leads-import-loading">
          <div className="leads-import-pulse" />
          <div>Importing {checkedRows.length} leads…</div>
        </div>
      )}

      {phase === 'done' && result && (
        <div className="leads-import-stage leads-import-done">
          <div className="leads-import-done-icon">
            <CheckIcon />
          </div>
          <div className="leads-import-done-headline">
            {result.inserted.length} lead{result.inserted.length === 1 ? '' : 's'} imported
          </div>
          <div className="leads-import-done-meta">
            {result.cross_linked > 0 && <span>{result.cross_linked} cross-linked (multi-owner) · </span>}
            {result.skipped.length > 0 && <span>{result.skipped.length} skipped · </span>}
            {result.errors.length > 0 && (
              <span className="leads-import-summary-warn">{result.errors.length} errors</span>
            )}
          </div>
          {result.errors.length > 0 && (
            <ul className="leads-import-error-list">
              {result.errors.map((e, i) => (
                <li key={i}>Row {e.row_index}: {e.error}</li>
              ))}
            </ul>
          )}
          <div className="leads-import-actions">
            <button className="btn-primary" onClick={reset}>Import another</button>
            {result.inserted?.length > 0 && stStatus === 'idle' && (
              <button
                className="btn-primary leads-skiptrace-trigger"
                onClick={importSkipTraceCheck}
              >
                Skip-trace these {result.inserted.length} lead{result.inserted.length === 1 ? '' : 's'}
              </button>
            )}
          </div>
          {stStatus === 'checking' && (
            <div className="leads-skiptrace-bar">
              <div className="leads-import-pulse" />
              <span>Checking…</span>
            </div>
          )}
          {stStatus === 'confirm' && stEstimate && (
            <div className="leads-skiptrace-bar">
              <span className="leads-skiptrace-bar-msg">
                Skip-trace {stEstimate.eligibleCount} lead{stEstimate.eligibleCount === 1 ? '' : 's'} for ~${(stEstimate.estCents / 100).toFixed(2)}?
              </span>
              <div className="leads-skiptrace-bar-actions">
                <button className="btn-primary leads-skiptrace-trigger" onClick={importSkipTraceRun}>Confirm</button>
                <button className="leads-filter-clear" onClick={importSkipTraceReset}>Cancel</button>
              </div>
            </div>
          )}
          {stStatus === 'running' && (
            <div className="leads-skiptrace-bar">
              <div className="leads-import-pulse" />
              <span>Skip-tracing…</span>
            </div>
          )}
          {stStatus === 'done' && stResult && (
            <div className="leads-skiptrace-bar">
              <span className="leads-skiptrace-bar-msg">
                {stResult.zeroEligible
                  ? 'These leads already have phones.'
                  : `${stResult.matched} of ${stResult.traced} got phones.`}
              </span>
              <button className="leads-filter-clear" onClick={importSkipTraceReset}>Dismiss</button>
            </div>
          )}
          {stError && (
            <div className="leads-import-error">{stError}</div>
          )}
        </div>
      )}
    </div>
  );
}
